import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { NikoProjectExtended } from '../../types/project';
import { useCursorContext } from '../../hooks/useCursorContext';
import { getAdjacentProjects } from '../../data/projectsDetailData';

interface ProjectNavigationProps {
  projectId: string;
}

interface NavLinkProps {
  project: NikoProjectExtended | undefined;
  direction: 'prev' | 'next';
}

function NavLink({ project, direction }: NavLinkProps) {
  const { setCursorState, setCursorText } = useCursorContext();
  const isPrev = direction === 'prev';

  if (!project) {
    return <div className="flex-1" />;
  }

  const handleHover = (hovering: boolean) => {
    if (hovering) {
      setCursorState('text');
      setCursorText(isPrev ? 'Prev' : 'Next');
    } else {
      setCursorState('default');
      setCursorText('');
    }
  };

  return (
    <Link
      to={`/project/${project.id}`}
      className={`flex-1 group block p-8 border border-white/10 rounded-xl hover:border-emerald-500/30 transition-colors ${
        isPrev ? '' : 'text-right'
      }`}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      {/* Direction label */}
      <span className="text-xs text-white/30 tracking-[0.3em] uppercase flex items-center gap-2">
        {isPrev && <ArrowLeft className="w-4 h-4" />}
        {isPrev ? 'Vorheriges Projekt' : 'Nächstes Projekt'}
        {!isPrev && <ArrowRight className="w-4 h-4 ml-auto" />}
      </span>

      {/* Project title */}
      <h3 className="mt-4 text-xl md:text-2xl text-white font-light group-hover:text-emerald-500 transition-colors">
        {project.title}
      </h3>

      {/* Company */}
      <p className="mt-2 text-sm text-white/40">{project.company}</p>
    </Link>
  );
}

export function ProjectNavigation({ projectId }: ProjectNavigationProps) {
  const { prev, next } = getAdjacentProjects(projectId);
  const { setCursorState } = useCursorContext();

  return (
    <section className="py-24 px-8 md:px-12 lg:px-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8">
          <NavLink project={prev} direction="prev" />
          <NavLink project={next} direction="next" />
        </div>

        {/* Back to all projects */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <Link
            to="/#work"
            className="inline-flex items-center gap-3 text-white/40 hover:text-emerald-500 transition-colors group"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-2 transition-transform" />
            <span className="text-sm tracking-wider uppercase">
              Alle Projekte
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
